import { AppThunk } from '../app/store';
import {
  messagingActions,
  TextFrameTransmissionMessage,
} from './messagingSlice';
import { orderActions } from './orderSlice';
import {
  getSpokenTotalCartFingerprint,
  getValidCartItems,
} from '../utils/cart';
import { orderTotalToCents, prepareSpokenPrompt } from '../utils/TTSMessage';
import logger from '../utils/logger';

/**
 * HITL-1587: THE send path for a spoken brand-menu prompt. Every sender of a
 * prompt that can carry `{total}` routes through this thunk — the omnibar's
 * `sendMessage`, the cart-add `/upsell` auto-prompt (menuSlice), and the
 * center-pane mod-group heading (useTTSMessage) — so the placeholder
 * substitution, the no-total skip, and the spoken-total bookkeeping cannot
 * drift apart per path (each of the latter two shipped without `{total}`
 * substitution and spoke raw braces in production).
 *
 * What it does, in order:
 * - resolves `{RESTAURANT_NAME}` and `{total}`;
 * - a `{total}`-templated prompt with no check total is skipped outright —
 *   braces are never spoken — leaving a METRIC in the session log;
 * - an actually-substituted total is recorded with a cart fingerprint so
 *   Complete skips the duplicate total readback while the cart is unchanged;
 * - sends the resolved text as a final TTS frame.
 *
 * Returns whether the prompt was spoken and, when it was, the resolved text
 * that went out — callers that echo a `spoken_text` record (the AI-suggestion
 * accept) reuse it instead of re-resolving. The TTS_SENT agent interception is
 * deliberately NOT sent here: its side effects differ per surface (the hooks
 * add task-status and first-interception handling via useAgentInterception;
 * menuSlice dispatches the bare event), so each caller stamps its own on a
 * spoken return.
 */
export const sendSpokenPrompt =
  ({
    message,
    metadata,
    skipMetricMessage,
  }: {
    message: string;
    metadata?: any;
    // The METRIC text logged when the prompt is skipped for want of a check
    // total; callers with more context (e.g. the upsell intent) override it.
    skipMetricMessage?: string;
  }): AppThunk<{ spoken: boolean; message: string | null }> =>
  (dispatch, getState) => {
    const state = getState();
    const { restaurantCode = '', restaurantName = '' } =
      state.restaurant.selectedRestaurantDetails || {};
    const prepared = prepareSpokenPrompt({
      message,
      total: orderTotalToCents(state.order.total),
      restaurantCode,
      restaurantName,
    });
    if (prepared.skip) {
      dispatch(
        messagingActions.sendInfo({
          data: {
            message:
              skipMetricMessage ??
              'TTS message skipped: no check total for its {total} template',
            type: 'METRIC',
          },
        } as any)
      );
      logger.debug(
        `[TTS] skipped {total}-templated message with no check total: "${message}"`
      );
      return { spoken: false, message: null };
    }
    if (prepared.totalSpoken) {
      dispatch(
        orderActions.setTotalSpokenInUpsell({
          spoken: true,
          cartFingerprint: getSpokenTotalCartFingerprint(
            getValidCartItems(state.cart.cartItems),
            state.cart.cartItemsQuantity
          ),
        })
      );
    }
    const payload: Partial<TextFrameTransmissionMessage> = {
      data: { payload: prepared.message, status: 'final', metadata },
    };
    dispatch(messagingActions.sendMessage(payload as any));
    return { spoken: true, message: prepared.message };
  };
